/**
 * Admin → Alerts (Deivydas 2026-10-13): each alert type's switch, threshold
 * and recipients; a test email; a manual run of the check; and what was sent.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from './client';

export interface AlertTypeRow {
  type: string;
  label: string;
  description: string;
  unit: 'hours' | 'minutes' | 'replies';
  defaultThreshold: number;
  min: number;
  max: number;
  enabled: boolean;
  threshold: number;
  recipients: string[];
}

export interface AlertsResponse {
  types: AlertTypeRow[];
  email: { configured: boolean; from?: string; host?: string };
  recent: Array<{
    at: string;
    type: string | null;
    ticketId: string | null;
    externalId: string | null;
    brand: string | null;
    detail: string | null;
  }>;
}

export interface SweepResult {
  candidates: number;
  droppedByZendesk: number;
  alreadySent: number;
  byType: Record<string, { tickets: string[]; sent: boolean; skipped?: string }>;
}

const KEY = ['admin', 'alerts'];

export const useAlerts = () =>
  useQuery({ queryKey: KEY, queryFn: () => apiGet<AlertsResponse>('/api/admin/alerts') });

export const useUpdateAlert = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ type, ...body }: { type: string; enabled?: boolean; threshold?: number; recipients?: string[] }) =>
      apiPatch(`/api/admin/alerts/${type}`, body),
    onSuccess: () => client.invalidateQueries({ queryKey: KEY }),
  });
};

export const useTestAlert = () =>
  useMutation({
    mutationFn: ({ type }: { type: string }) =>
      apiPost<{ sentTo: string[] }>(`/api/admin/alerts/${type}/test`, {}),
  });

export const useRunAlertSweep = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ dryRun }: { dryRun: boolean }) =>
      apiPost<SweepResult>(`/api/admin/alerts/sweep?dryRun=${dryRun ? 'true' : 'false'}`, {}),
    onSuccess: () => client.invalidateQueries({ queryKey: KEY }),
  });
};
